import CheckIcon from "@mui/icons-material/Check";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { Box, IconButton, Tooltip, useTheme } from "@mui/material";
import { SystemStyleObject, Theme } from "@mui/system";
import React, { useEffect, useState } from "react";
import CustomTypography from "./form/CustomTypography";

const emailCopyRowRootSx: SystemStyleObject<Theme> = {
  display: "flex",
  alignItems: "flex-start",
  gap: 0.75,
  minWidth: 0,
  maxWidth: "100%",
};

const emailCopyRowWrapSx: SystemStyleObject<Theme> = {
  whiteSpace: "normal",
  wordBreak: "break-all",
  overflowWrap: "anywhere",
  lineHeight: 1.35,
  minWidth: 0,
};

const emailCopyRowEllipsisSx: SystemStyleObject<Theme> = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

export interface EmailCopyRowProps {
  email: string | null | undefined;
  emptyFallback?: string;
  /** Tooltip / aria label for the copy action. Default: "Copy email" */
  copyLabel?: string;
  /** Show the leading mail icon. Default: true */
  showIcon?: boolean;
  fontWeight?: number;
  color?: string;
  /** Colour for the mail + copy icons. Default: `customColors.mediumGray` */
  iconColor?: string;
  variant?: "body2" | "body3" | "heading6" | "heading7";
  /** When false, the email stays on one line with ellipsis + tooltip. Default: true */
  allowWrap?: boolean;
  /** Extra styles merged after the component's own text CSS. */
  sx?: SystemStyleObject<Theme>;
}

/**
 * Displays an email id with a leading mail icon and a one-click copy button.
 * Self-contained — no parent copy state required. Copy never bubbles to the row.
 */
const EmailCopyRow: React.FC<EmailCopyRowProps> = ({
  email,
  emptyFallback = "-",
  copyLabel = "Copy email",
  showIcon = true,
  fontWeight,
  color,
  iconColor,
  variant = "body3",
  allowWrap = true,
  sx,
}) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const value = email?.trim() || "";
  const [copied, setCopied] = useState(false);
  const textColor = color ?? colors.darkBlueText;
  const mutedIconColor = iconColor ?? colors.mediumGray;

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // ignore clipboard errors
    }
  };

  const mergedTextSx: SystemStyleObject<Theme> = {
    fontSize: "0.75rem",
    ...(allowWrap ? emailCopyRowWrapSx : emailCopyRowEllipsisSx),
    ...sx,
  };

  const icon = showIcon ? (
    <Box
      sx={{
        color: mutedIconColor,
        display: "flex",
        flexShrink: 0,
        pt: "1px",
        "& svg": { fontSize: 14 },
      }}
    >
      <EmailOutlinedIcon />
    </Box>
  ) : null;

  if (!value) {
    return (
      <Box sx={emailCopyRowRootSx}>
        {icon}
        <CustomTypography
          variant={variant}
          color={colors.greyTextColor}
          sx={mergedTextSx}
        >
          {emptyFallback}
        </CustomTypography>
      </Box>
    );
  }

  return (
    <Box sx={emailCopyRowRootSx}>
      {icon}
      <Tooltip title={allowWrap ? "" : value}>
        <CustomTypography
          variant={variant}
          fontWeight={fontWeight}
          color={textColor}
          sx={mergedTextSx}
        >
          {value}
        </CustomTypography>
      </Tooltip>
      <Tooltip title={copied ? "Copied" : copyLabel}>
        <IconButton
          size="small"
          onClick={(e) => {
            void handleCopy(e);
          }}
          aria-label={`${copyLabel} ${value}`}
          sx={{
            p: 0.3,
            mt: "-2px",
            flexShrink: 0,
            color: copied ? colors.purpleMain : mutedIconColor,
            transition: "color 0.15s ease",
            "&:hover": { color: colors.purpleMain },
          }}
        >
          {copied ? (
            <CheckIcon sx={{ fontSize: 12 }} />
          ) : (
            <ContentCopyOutlinedIcon sx={{ fontSize: 12 }} />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default EmailCopyRow;
